import { forwardRef, memo, useMemo, useCallback, useState, useEffect, useRef, useId } from 'react'
import { AgGridReact } from 'ag-grid-react'
import {
  ModuleRegistry,
  AllCommunityModule,
} from 'ag-grid-community'
import * as XLSX from 'xlsx'
import type {
  ColDef,
  GridReadyEvent,
  RowClickedEvent,
  RowClassParams,
  CellClickedEvent,
  GridApi,
  CellMouseOverEvent,
  Column,
} from 'ag-grid-community'
import type { AgGridReactProps } from 'ag-grid-react'
import { AG_GRID_LOCALE_KR } from '@/locales/ag-grid-ko-KR'
import { designTokens } from '@/utils/design-tokens'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import '@/styles/ag-grid-custom.css'
import { themeQuartz, iconSetQuartzBold } from 'ag-grid-community'
import { useAlert } from '@/components/ui/Alert/exports'


// AG-Grid 모듈 등록
ModuleRegistry.registerModules([AllCommunityModule])

// Helper function to get nested object value by dot notation path
const getNestedValue = (obj: any, path: string): any => {
  if (!obj || !path) return undefined
  return path.split('.').reduce((current, key) => current?.[key], obj)
}

const customTheme = themeQuartz
  .withPart(iconSetQuartzBold)
  .withParams({
    browserColorScheme: 'light',
    fontFamily: 'inherit',
    headerFontSize: 14,
    headerFontWeight: 700,
    oddRowBackgroundColor: '#F5F5F5',
  })

// 셀 위치 타입
interface CellPosition {
  rowIndex: number
  colId: string
}

// 범위 선택 상태 타입
interface RangeSelectionState {
  start: CellPosition | null
  end: CellPosition | null
  isSelecting: boolean
}

// 선택된 범위 정보 타입
export interface SelectedRange {
  startRowIndex: number
  endRowIndex: number
  startColIndex: number
  endColIndex: number
  cells: Array<{ rowIndex: number; colId: string; value: any }>
}

interface DataGridProps extends Omit<AgGridReactProps, 'theme'> {
  height?: string | number;
  className?: string;
  onGridReady?: (params: GridReadyEvent) => void;
  onRowClicked?: (event: RowClickedEvent) => void;
  // Pagination props
  pagination?: boolean;
  paginationPageSize?: number;
  paginationPageSizeSelector?: number[] | boolean;
  paginationAutoPageSize?: boolean;
  // Server-side pagination support
  rowModelType?: 'clientSide' | 'serverSide' | 'viewport' | 'infinite';
  serverSideDatasource?: any;
  datasource?: any; // infinite row model용
  cacheBlockSize?: number;
  maxBlocksInCache?: number;
  infiniteInitialRowCount?: number;
  maxConcurrentDatasourceRequests?: number;
  // Loading overlay
  loading?: boolean;
  // Cell copy functionality
  enableCellCopy?: boolean; // Enable Ctrl+C copy functionality (default: true)
  cellCopyFormatter?: (params: CellClickedEvent) => string; // Custom formatter for clipboard
  cellCopyExcludeColumns?: string[]; // Columns to exclude from copy (e.g., ['actions'])
  // Range selection functionality (custom implementation)
  enableRangeSelection?: boolean; // Enable drag range selection (default: false)
  rangeSelectionExcludeColumns?: string[]; // Columns to exclude from range selection
  onCustomRangeSelectionChanged?: (range: SelectedRange | null) => void; // Range change callback
  // Range copy formatter (for formatting specific columns during range copy)
  rangeCopyFormatter?: (colId: string, value: any) => string;
  // Header copy functionality (Shift+Ctrl+C)
  enableHeaderCopy?: boolean; // Enable Shift+Ctrl+C to copy with headers (default: true)
  // Excel download functionality
  enableExcelDownload?: boolean; // Enable Excel download in context menu (default: true)
  excelFileName?: string; // Excel file name (default: 'data')
  excelExcludeColumns?: string[]; // Columns to exclude from Excel (e.g., ['actions'])
}

const DataGrid = memo(forwardRef<AgGridReact, DataGridProps>(({
                                                           height = '600px',
                                                           className = '',
                                                           columnDefs,
                                                           rowData,
                                                           defaultColDef: customDefaultColDef,
                                                           onGridReady,
                                                           onRowClicked,
                                                           onCellClicked,
                                                           rowSelection = 'single',
                                                           suppressRowClickSelection = false,
                                                           animateRows = true,
                                                           headerHeight = 48,
                                                           rowHeight = 52,
                                                           getRowStyle,
                                                           // Pagination props
                                                           pagination = false,
                                                           paginationPageSize = 20,
                                                           paginationPageSizeSelector = [10, 20, 50, 100],
                                                           paginationAutoPageSize = false,
                                                           // Server-side pagination props
                                                           rowModelType = 'clientSide',
                                                           serverSideDatasource,
                                                           datasource,
                                                           cacheBlockSize,
                                                           maxBlocksInCache,
                                                           infiniteInitialRowCount,
                                                           maxConcurrentDatasourceRequests,
                                                           // Loading overlay
                                                           loading = false,
                                                           // Cell copy props
                                                           enableCellCopy = true,
                                                           cellCopyFormatter,
                                                           cellCopyExcludeColumns = ['actions', 'action'],
                                                           // Range selection props
                                                           enableRangeSelection = false,
                                                           rangeSelectionExcludeColumns = ['actions', 'action'],
                                                           onCustomRangeSelectionChanged,
                                                           rangeCopyFormatter,
                                                           // Header copy props
                                                           enableHeaderCopy = true,
                                                           // Excel download props
                                                           enableExcelDownload = true,
                                                           excelFileName = 'data',
                                                           excelExcludeColumns = ['actions', 'action'],
                                                           ...props
                                                         }, ref) => {
  const { showSuccess, showError } = useAlert()
  const [focusedCell, setFocusedCell] = useState<CellClickedEvent | null>(null)
  const [gridApi, setGridApi] = useState<GridApi | null>(null)

  // 고유 ID 생성 (여러 DataGrid가 있을 때 각각을 구분하기 위함)
  const gridId = useId()
  const gridContainerRef = useRef<HTMLDivElement>(null)

  // Range selection state
  const [rangeSelection, setRangeSelection] = useState<RangeSelectionState>({
    start: null,
    end: null,
    isSelecting: false,
  })

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    visible: boolean
    x: number
    y: number
  }>({ visible: false, x: 0, y: 0 })
  // Ref to always have latest rangeSelection for cellStyle callback
  const rangeSelectionRef = useRef<RangeSelectionState>(rangeSelection)
  rangeSelectionRef.current = rangeSelection

  // Ref for tracking selection version to trigger re-renders
  const selectionVersionRef = useRef(0)
  const [selectionVersion, setSelectionVersion] = useState(0)

  const defaultColDef: ColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    headerHeight: 48,
    rowHeight: 52,
    ...customDefaultColDef,
  }), [customDefaultColDef])

  const handleGridReady = useCallback((params: GridReadyEvent) => {
    params.api.sizeColumnsToFit()
    setGridApi(params.api)
    onGridReady?.(params)
  }, [onGridReady])

  const handleRowClicked = useCallback((event: RowClickedEvent) => {
    onRowClicked?.(event)
  }, [onRowClicked])

  const defaultGetRowStyle = useCallback((params: RowClassParams) => {
    if (params.node.isSelected()) {
      return { backgroundColor: designTokens.colors.info.light }
    }
    return undefined
  }, [])

  // Calculate selected range from start/end positions
  const calculateSelectedRange = useCallback((): SelectedRange | null => {
    if (!rangeSelection.start || !rangeSelection.end || !gridApi) return null

    const columns = gridApi.getColumns() || []
    const colIds = columns.map((col: Column) => col.getColId())

    // Filter out excluded columns
    const validColIds = colIds.filter(id => !rangeSelectionExcludeColumns?.includes(id))

    const startColIdx = validColIds.indexOf(rangeSelection.start.colId)
    const endColIdx = validColIds.indexOf(rangeSelection.end.colId)

    // If either column is not in valid columns, return null
    if (startColIdx === -1 || endColIdx === -1) return null

    // Normalize indices (min/max)
    const startRowIdx = Math.min(rangeSelection.start.rowIndex, rangeSelection.end.rowIndex)
    const endRowIdx = Math.max(rangeSelection.start.rowIndex, rangeSelection.end.rowIndex)
    const minColIdx = Math.min(startColIdx, endColIdx)
    const maxColIdx = Math.max(startColIdx, endColIdx)

    // Collect cell data
    const cells: SelectedRange['cells'] = []
    for (let rowIdx = startRowIdx; rowIdx <= endRowIdx; rowIdx++) {
      const rowNode = gridApi.getDisplayedRowAtIndex(rowIdx)
      if (!rowNode || rowNode.data?.isPlaceholder) continue

      for (let colIdx = minColIdx; colIdx <= maxColIdx; colIdx++) {
        const colId = validColIds[colIdx]
        const column = gridApi.getColumn(colId)
        let value

        // Try to use valueGetter from column definition if available
        if (column) {
          const colDef = column.getColDef()
          if (colDef.valueGetter && typeof colDef.valueGetter === 'function') {
            // Call valueGetter with params object
            value = colDef.valueGetter({
              data: rowNode.data,
              node: rowNode,
              colDef,
              column,
              api: gridApi,
              context: undefined,
              getValue: (field: string) => getNestedValue(rowNode.data, field),
            } as any)
          } else {
            // Fall back to field value or nested path
            value = colDef.field
              ? getNestedValue(rowNode.data, colDef.field)
              : getNestedValue(rowNode.data, colId)
          }
        } else {
          value = getNestedValue(rowNode.data, colId)
        }

        cells.push({
          rowIndex: rowIdx,
          colId,
          value,
        })
      }
    }

    return {
      startRowIndex: startRowIdx,
      endRowIndex: endRowIdx,
      startColIndex: minColIdx,
      endColIndex: maxColIdx,
      cells,
    }
  }, [rangeSelection, gridApi, rangeSelectionExcludeColumns])

  // Clear range selection
  const clearRangeSelection = useCallback(() => {
    setRangeSelection({ start: null, end: null, isSelecting: false })
    selectionVersionRef.current += 1
    setSelectionVersion(selectionVersionRef.current)
    onCustomRangeSelectionChanged?.(null)
  }, [onCustomRangeSelectionChanged])

  // Cell click handler for copy functionality and range selection start
  const handleCellClick = useCallback((params: CellClickedEvent) => {
    // Call parent's onCellClicked if provided
    onCellClicked?.(params)

    // Ignore right-click to preserve existing selection for context menu
    if ((params.event as MouseEvent)?.button === 2) return

    const colId = params.column.getColId()

    // Handle range selection
    if (enableRangeSelection) {
      // Skip excluded columns
      if (rangeSelectionExcludeColumns?.includes(colId)) return
      // Skip placeholder rows
      if (params.data?.isPlaceholder) return

      // Start new selection on click (not drag)
      if (!rangeSelection.isSelecting) {
        setRangeSelection({
          start: { rowIndex: params.rowIndex!, colId },
          end: { rowIndex: params.rowIndex!, colId },
          isSelecting: false,
        })
        selectionVersionRef.current += 1
        setSelectionVersion(selectionVersionRef.current)
      }
    }

    // Handle cell copy functionality if enabled
    if (enableCellCopy) {
      // Skip excluded columns (e.g., actions)
      if (cellCopyExcludeColumns.includes(colId)) return

      // Skip placeholder rows (isPlaceholder property check)
      if (params.data?.isPlaceholder) return

      // Store focused cell for Ctrl+C copy
      setFocusedCell(params)
    }
  }, [onCellClicked, enableCellCopy, cellCopyExcludeColumns, enableRangeSelection, rangeSelectionExcludeColumns, rangeSelection.isSelecting])

  // Mouse down handler for starting drag selection
  const handleCellMouseDown = useCallback((event: any) => {
    if (!enableRangeSelection) return

    // Ignore right-click to preserve existing selection for context menu
    if (event.event?.button === 2) return

    const colId = event.column?.getColId()
    if (!colId) return
    if (rangeSelectionExcludeColumns?.includes(colId)) return
    if (event.data?.isPlaceholder) return

    // Start drag selection
    setRangeSelection({
      start: { rowIndex: event.rowIndex, colId },
      end: { rowIndex: event.rowIndex, colId },
      isSelecting: true,
    })
    selectionVersionRef.current += 1
    setSelectionVersion(selectionVersionRef.current)
  }, [enableRangeSelection, rangeSelectionExcludeColumns])

  // Mouse over handler for updating drag selection
  const handleCellMouseOver = useCallback((event: CellMouseOverEvent) => {
    if (!enableRangeSelection || !rangeSelection.isSelecting) return

    const colId = event.column?.getColId()
    if (!colId) return
    if (rangeSelectionExcludeColumns?.includes(colId)) return
    if (event.data?.isPlaceholder) return
    if (event.rowIndex === null || event.rowIndex === undefined) return

    setRangeSelection(prev => ({
      ...prev,
      end: { rowIndex: event.rowIndex!, colId },
    }))
    selectionVersionRef.current += 1
    setSelectionVersion(selectionVersionRef.current)
  }, [enableRangeSelection, rangeSelection.isSelecting, rangeSelectionExcludeColumns])

  // Global mouse up handler for ending drag selection
  useEffect(() => {
    if (!enableRangeSelection) return

    const handleMouseUp = () => {
      if (rangeSelection.isSelecting) {
        setRangeSelection(prev => ({ ...prev, isSelecting: false }))
        // Notify parent of selection change
        const range = calculateSelectedRange()
        onCustomRangeSelectionChanged?.(range)
      }
    }

    window.addEventListener('mouseup', handleMouseUp)
    return () => window.removeEventListener('mouseup', handleMouseUp)
  }, [enableRangeSelection, rangeSelection.isSelecting, calculateSelectedRange, onCustomRangeSelectionChanged])

  // Close context menu
  const closeContextMenu = useCallback(() => {
    setContextMenu({ visible: false, x: 0, y: 0 })
  }, [])

  // Context menu handler
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    // Show context menu if range selection is enabled OR excel download is enabled
    if (!enableRangeSelection && !enableExcelDownload) return

    e.preventDefault()
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
    })
  }, [enableRangeSelection, enableExcelDownload])

  // ESC key handler for clearing selection and closing context menu
  useEffect(() => {
    if (!enableRangeSelection) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (contextMenu.visible) {
          closeContextMenu()
        } else {
          clearRangeSelection()
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [enableRangeSelection, clearRangeSelection, contextMenu.visible, closeContextMenu])

  // Close context menu on outside click
  useEffect(() => {
    if (!contextMenu.visible) return

    const handleClick = () => closeContextMenu()
    const handleScroll = () => closeContextMenu()

    window.addEventListener('click', handleClick)
    window.addEventListener('scroll', handleScroll, true)
    return () => {
      window.removeEventListener('click', handleClick)
      window.removeEventListener('scroll', handleScroll, true)
    }
  }, [contextMenu.visible, closeContextMenu])

  // Default clipboard formatter
  const defaultCellCopyFormatter = useCallback((params: CellClickedEvent): string => {
    const value = params.value

    // Handle common React component types (Tag, etc.)
    if (value === null || value === undefined) {
      return ''
    }

    // Handle Date objects
    if (value instanceof Date) {
      return value.toLocaleString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    // Handle boolean (for status columns, etc.)
    if (typeof value === 'boolean') {
      return value ? '활성' : '비활성'
    }

    // Default: convert to string
    return value.toString()
  }, [])

  // Default range copy formatter
  const defaultRangeCopyFormatter = useCallback((_colId: string, value: any): string => {
    if (value === null || value === undefined) {
      return ''
    }

    if (value instanceof Date) {
      return value.toLocaleString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    if (typeof value === 'boolean') {
      return value ? '활성' : '비활성'
    }

    return value.toString()
  }, [])

  // Copy range to clipboard as TSV
  const copyRangeToClipboard = useCallback(async (includeHeaders: boolean = false) => {
    const range = calculateSelectedRange()
    if (!range || range.cells.length === 0) return false

    // Build TSV content
    const rows: string[][] = []
    let currentRowIdx = -1
    let currentRow: string[] = []

    // Get unique column IDs in order from cells
    const colIdsInRange: string[] = []
    for (const cell of range.cells) {
      if (!colIdsInRange.includes(cell.colId)) {
        colIdsInRange.push(cell.colId)
      }
    }

    // Add header row if requested
    if (includeHeaders && gridApi) {
      const headerRow: string[] = []
      for (const colId of colIdsInRange) {
        const column = gridApi.getColumn(colId)
        if (column) {
          const colDef = column.getColDef()
          headerRow.push(colDef.headerName || colId)
        } else {
          headerRow.push(colId)
        }
      }
      rows.push(headerRow)
    }

    for (const cell of range.cells) {
      if (cell.rowIndex !== currentRowIdx) {
        if (currentRow.length > 0) rows.push(currentRow)
        currentRow = []
        currentRowIdx = cell.rowIndex
      }

      // Format cell value
      const formatter = rangeCopyFormatter || defaultRangeCopyFormatter
      const formattedValue = formatter(cell.colId, cell.value)
      currentRow.push(formattedValue)
    }
    if (currentRow.length > 0) rows.push(currentRow)

    // Generate TSV string
    const tsvContent = rows.map(row => row.join('\t')).join('\n')

    try {
      await navigator.clipboard.writeText(tsvContent)
      const dataRowCount = includeHeaders ? rows.length - 1 : rows.length
      const colCount = colIdsInRange.length
      const cellCount = dataRowCount * colCount
      const headerText = includeHeaders ? ' (헤더 포함)' : ''
      showSuccess(`${dataRowCount}행 × ${colCount}열 (${cellCount}개 셀) 복사됨${headerText}`)
      return true
    } catch (err) {
      console.error('Clipboard copy failed:', err)
      showError('복사에 실패했습니다')
      return false
    }
  }, [calculateSelectedRange, rangeCopyFormatter, defaultRangeCopyFormatter, gridApi, showSuccess, showError])

  // Export to Excel (XLSX)
  const exportToExcel = useCallback(() => {
    if (!gridApi || !columnDefs) return

    try {
      // Get all displayed rows
      const rowsData: any[] = []
      gridApi.forEachNodeAfterFilterAndSort((node) => {
        if (node.data && !node.data.isPlaceholder) {
          rowsData.push(node.data)
        }
      })

      if (rowsData.length === 0) {
        showError('내보낼 데이터가 없습니다')
        return
      }

      // Get columns (exclude specified columns)
      const columns = (columnDefs as ColDef[]).filter(col => {
        const colId = col.field || col.colId
        return colId && !excelExcludeColumns?.includes(colId)
      })

      // Build header row
      const headers = columns.map(col => col.headerName || col.field || '')

      // Build data rows
      const data = rowsData.map(row => {
        return columns.map(col => {
          const colId = col.field || col.colId
          if (!colId) return ''

          let value
          // Use valueGetter if available
          if (col.valueGetter && typeof col.valueGetter === 'function') {
            const column = gridApi.getColumn(colId)
            const rowNode = gridApi.getRowNode(row.id)
            value = col.valueGetter({
              data: row,
              node: rowNode,
              colDef: col,
              column,
              api: gridApi,
              context: undefined,
              getValue: (field: string) => getNestedValue(row, field),
            } as any)
          } else {
            value = getNestedValue(row, colId)
          }

          // Format value for Excel
          if (value === null || value === undefined) return ''
          if (value instanceof Date) {
            return value.toLocaleDateString('ko-KR')
          }
          return value
        })
      })

      // Create worksheet
      const wsData = [headers, ...data]
      const ws = XLSX.utils.aoa_to_sheet(wsData)

      // Auto-size columns
      const colWidths = headers.map((header, idx) => {
        const maxLength = Math.max(
          header.length,
          ...data.map(row => String(row[idx] || '').length)
        )
        return { wch: Math.min(Math.max(maxLength + 2, 10), 50) }
      })
      ws['!cols'] = colWidths

      // Create workbook and download
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1')

      const fileName = `${excelFileName}_${new Date().toISOString().split('T')[0]}.xlsx`
      XLSX.writeFile(wb, fileName)

      showSuccess(`${rowsData.length}행 엑셀 다운로드 완료`)
    } catch (err) {
      console.error('Excel export failed:', err)
      showError('엑셀 다운로드에 실패했습니다')
    }
  }, [gridApi, columnDefs, excelExcludeColumns, excelFileName, showSuccess, showError])

  // Keyboard event listener for Ctrl+C copy (and Shift+Ctrl+C for headers)
  useEffect(() => {
    if (!enableCellCopy) return

    const handleKeyDown = (e: KeyboardEvent) => {
      // Check for Ctrl+C or Cmd+C (with optional Shift for headers)
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        // Don't prevent default if user is selecting text
        const selection = window.getSelection()
        if (selection && selection.toString().length > 0) {
          return // Let browser handle text selection copy
        }

        // Check if Shift is pressed for header copy
        const includeHeaders = e.shiftKey && enableHeaderCopy

        // Check if we have a range selection with multiple cells
        if (enableRangeSelection) {
          const range = calculateSelectedRange()
          if (range && range.cells.length > 1) {
            e.preventDefault()
            copyRangeToClipboard(includeHeaders)
            return
          }
        }

        // Fall back to single cell copy (no header for single cell)
        if (focusedCell) {
          e.preventDefault()

          // Use custom formatter if provided, otherwise use default
          const formatter = cellCopyFormatter || defaultCellCopyFormatter
          const text = formatter(focusedCell)

          // Copy to clipboard
          navigator.clipboard.writeText(text)
            .then(() => {
              showSuccess('복사되었습니다')
            })
            .catch((err) => {
              console.error('Clipboard copy failed:', err)
              showError('복사에 실패했습니다')
            })
        }
      }
    }

    // Add event listener
    window.addEventListener('keydown', handleKeyDown)

    // Cleanup on unmount
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [enableCellCopy, enableRangeSelection, enableHeaderCopy, focusedCell, cellCopyFormatter, defaultCellCopyFormatter, calculateSelectedRange, copyRangeToClipboard, showSuccess, showError])

  // Update cell highlights via DOM manipulation (no flickering)
  const updateCellHighlights = useCallback(() => {
    if (!enableRangeSelection || !gridApi) return

    // Use ref to find this specific grid container (supports multiple grids on page)
    const gridContainer = gridContainerRef.current
    if (!gridContainer) return

    // Remove all existing highlights in THIS grid only
    const highlightedCells = gridContainer.querySelectorAll('.ag-cell-range-selected')
    highlightedCells.forEach((cell: Element) => {
      cell.classList.remove('ag-cell-range-selected')
    })

    const currentSelection = rangeSelectionRef.current
    if (!currentSelection.start || !currentSelection.end) return

    // Calculate range bounds
    const columns = gridApi.getColumns() || []
    const colIds = columns.map((col: Column) => col.getColId())
    const validColIds = colIds.filter(id => !rangeSelectionExcludeColumns?.includes(id))

    const startColIdx = validColIds.indexOf(currentSelection.start.colId)
    const endColIdx = validColIds.indexOf(currentSelection.end.colId)
    if (startColIdx === -1 || endColIdx === -1) return

    const startRowIdx = Math.min(currentSelection.start.rowIndex, currentSelection.end.rowIndex)
    const endRowIdx = Math.max(currentSelection.start.rowIndex, currentSelection.end.rowIndex)
    const minColIdx = Math.min(startColIdx, endColIdx)
    const maxColIdx = Math.max(startColIdx, endColIdx)

    // Add highlight class to cells in range
    for (let rowIdx = startRowIdx; rowIdx <= endRowIdx; rowIdx++) {
      const rowNode = gridApi.getDisplayedRowAtIndex(rowIdx)
      if (!rowNode || rowNode.data?.isPlaceholder) continue

      for (let colIdx = minColIdx; colIdx <= maxColIdx; colIdx++) {
        const colId = validColIds[colIdx]
        // Use DOM query selector to find the cell element within THIS grid
        const cellElement = gridContainer.querySelector(
          `.ag-row[row-index="${rowIdx}"] .ag-cell[col-id="${colId}"]`
        )
        if (cellElement) {
          cellElement.classList.add('ag-cell-range-selected')
        }
      }
    }
  }, [enableRangeSelection, gridApi, rangeSelectionExcludeColumns])

  // Update highlights when selection changes
  useEffect(() => {
    if (enableRangeSelection && selectionVersion >= 0) {
      // Use requestAnimationFrame for smooth updates
      requestAnimationFrame(() => {
        updateCellHighlights()
      })
    }
  }, [enableRangeSelection, selectionVersion, updateCellHighlights])

  return (
    <div
      ref={gridContainerRef}
      className={`ag-theme-quartz ${className}`}
      data-grid-id={gridId}
      style={{
        height,
        width: '100%',
        userSelect: enableRangeSelection ? 'none' : undefined,
      }}
      onContextMenu={handleContextMenu}
    >
      <AgGridReact
        ref={ref}
        theme={customTheme}
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        onGridReady={handleGridReady}
        onRowClicked={handleRowClicked}
        onCellClicked={handleCellClick}
        onCellMouseDown={handleCellMouseDown}
        onCellMouseOver={handleCellMouseOver}
        rowSelection={enableRangeSelection ? undefined : rowSelection}
        suppressRowClickSelection={enableRangeSelection ? true : suppressRowClickSelection}
        animateRows={animateRows}
        headerHeight={headerHeight}
        rowHeight={rowHeight}
        localeText={props.localeText || AG_GRID_LOCALE_KR}
        getRowStyle={getRowStyle || defaultGetRowStyle}
        // Pagination props
        pagination={pagination}
        paginationPageSize={paginationPageSize}
        paginationPageSizeSelector={paginationPageSizeSelector}
        paginationAutoPageSize={paginationAutoPageSize}
        // Server-side pagination props
        rowModelType={rowModelType}
        serverSideDatasource={serverSideDatasource}
        datasource={datasource}
        cacheBlockSize={cacheBlockSize}
        maxBlocksInCache={maxBlocksInCache}
        infiniteInitialRowCount={infiniteInitialRowCount}
        maxConcurrentDatasourceRequests={maxConcurrentDatasourceRequests}
        // Loading overlay
        loading={loading}
        {...props}
      />

      {/* Context Menu */}
      {contextMenu.visible && (
        <div
          style={{
            position: 'fixed',
            top: contextMenu.y,
            left: contextMenu.x,
            zIndex: 9999,
            backgroundColor: '#FFFFFF',
            border: `1px solid ${designTokens.colors.gray[2]}`,
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            minWidth: '200px',
            padding: '4px 0',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Copy buttons - only show when range selection exists */}
          {enableRangeSelection && calculateSelectedRange() && calculateSelectedRange()!.cells.length > 0 && (
            <>
              <button
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  padding: '8px 12px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '14px',
                  color: designTokens.colors.gray[6],
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = designTokens.colors.gray[0]
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent'
                }}
                onClick={() => {
                  copyRangeToClipboard(false)
                  closeContextMenu()
                }}
              >
                <span>복사</span>
                <span style={{ color: designTokens.colors.gray[4], fontSize: '12px' }}>Ctrl+C</span>
              </button>
              <button
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  padding: '8px 12px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '14px',
                  color: designTokens.colors.gray[6],
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = designTokens.colors.gray[0]
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent'
                }}
                onClick={() => {
                  copyRangeToClipboard(true)
                  closeContextMenu()
                }}
              >
                <span>헤더 포함 복사</span>
                <span style={{ color: designTokens.colors.gray[4], fontSize: '12px' }}>Shift+Ctrl+C</span>
              </button>
            </>
          )}
          {enableExcelDownload && (
            <>
              {/* Divider - only show if copy buttons are visible */}
              {enableRangeSelection && calculateSelectedRange() && calculateSelectedRange()!.cells.length > 0 && (
                <div style={{
                  height: '1px',
                  backgroundColor: designTokens.colors.gray[1],
                  margin: '4px 0',
                }} />
              )}
              <button
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  padding: '8px 12px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '14px',
                  color: designTokens.colors.gray[6],
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = designTokens.colors.gray[0]
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent'
                }}
                onClick={() => {
                  exportToExcel()
                  closeContextMenu()
                }}
              >
                <span>엑셀 다운로드</span>
                <span style={{ color: designTokens.colors.gray[4], fontSize: '12px' }}>.xlsx</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}))

DataGrid.displayName = 'DataGrid'

export default DataGrid
